import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/api';
import MockExaminer from '../components/examiner/MockExaminer';
import useExaminerVisibility from '../utils/useExaminerVisibility';

const Training = () => {
  const [mainTab, setMainTab] = useState('ppl');
  const [subTab, setSubTab] = useState('quizzes');
  const { currentUser } = useApp();
  const showExaminer = useExaminerVisibility();
  useEffect(() => { if (!showExaminer) setMainTab('ppl'); }, [showExaminer]);
  const [quizzes, setQuizzes] = useState([]);
  const [selectedQuizId, setSelectedQuizId] = useState('');
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [attempts, setAttempts] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [selectedAttemptId, setSelectedAttemptId] = useState('');
  const [attemptDetails, setAttemptDetails] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [currentAttempt, setCurrentAttempt] = useState(null);
  const [answerResults, setAnswerResults] = useState({});
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceiptImage, setSelectedReceiptImage] = useState('');

  useEffect(() => {
    if (currentUser?.sessionId && mainTab === 'ppl' && subTab === 'quizzes') {
      fetchQuizzes();
    }
  }, [currentUser?.sessionId, mainTab, subTab]);

  const formatLocalTime = (utcTimeString) => {
    if (!utcTimeString) return '';
    const utcDate = new Date(utcTimeString);
    const localDate = new Date(utcDate.getTime() - (utcDate.getTimezoneOffset() * 60000));
    return localDate.toLocaleString();
  };

  const fetchQuizzes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/quiz/category/PPL`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setQuizzes(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    }
  };

  const fetchQuizDetails = async (quizId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/quiz/${quizId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedQuiz(data);
        setUserAnswers({});
        setSubmitted(false);
      } else {
        setSelectedQuiz(null);
      }
    } catch (error) {
      setSelectedQuiz(null);
    }
    
    fetchAttempts(quizId);
  };

  const fetchAttemptDetails = async (attemptId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/quiz/attempt/${attemptId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        // Remove duplicates and use the attempt data directly since it already has correctAnswer and isCorrect
        if (data.answers) {
          const uniqueAnswers = data.answers.reduce((acc, answer) => {
            if (!acc.find(a => a.questionId === answer.questionId)) {
              acc.push(answer);
            }
            return acc;
          }, []);
          data.answers = uniqueAnswers;
        }
        setAttemptDetails(data);
      }
    } catch (error) {
      console.error('Error fetching attempt details:', error);
    }
  };

  const fetchAttempts = async (quizId) => {
    try {
      const userId = currentUser?.user?.id || currentUser?.id;
      if (!userId) return null;
      
      const isInstructor = currentUser?.user?.role === 'instructor' || currentUser?.user?.roleId === 3 || currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser';
      
      let endpoint;
      if (isInstructor) {
        endpoint = `${API_BASE_URL}/quiz/${quizId}/attempts`;
      } else {
        endpoint = `${API_BASE_URL}/quiz/attempts/${userId}/${quizId}`;
      }
      
      const response = await fetch(endpoint, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setAttempts(Array.isArray(data) ? data : []);
        
        const inProgressAttempt = data.find(attempt => attempt.status === 'in-progress' && attempt.userId === userId);
        if (inProgressAttempt) {
          setCurrentAttempt(inProgressAttempt);
          setSelectedAttemptId(inProgressAttempt.id);
        }
        return data;
      } else {
        setAttempts([]);
      }
    } catch (error) {
      setAttempts([]);
    }
  };

  const handleQuizSelect = (quizId) => {
    setSelectedQuizId(quizId);
    setSelectedAttemptId('');
    setAttemptDetails(null);
    setCurrentAttempt(null);
    setAnswerResults({});
    setUserAnswers({});
    setSubmitted(false);
    if (quizId) {
      fetchQuizDetails(quizId);
    } else {
      setSelectedQuiz(null);
      setAttempts([]);
    }
  };

  const submitAnswer = async (questionId, answer) => {
    let attempt = currentAttempt;
    if (!attempt) {
      attempt = await startQuizAttempt();
      if (!attempt) return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/quiz/attempt/${attempt.id}/question/${questionId}/answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({ selectedAnswer: answer })
      });
      
      if (response.ok) {
        const result = await response.json();
        setAnswerResults(prev => ({ ...prev, [questionId]: result }));
        setUserAnswers(prev => ({ ...prev, [questionId]: answer }));
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
    }
  };

  const startQuizAttempt = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/quiz/attempt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({ quizId: selectedQuiz.id })
      });
      
      if (response.ok) {
        const attempt = await response.json();
        const attemptWithDefaults = {
          ...attempt,
          score: 0,
          totalQuestions: selectedQuiz.questions.length,
          status: 'in-progress',
          firstName: currentUser?.user?.firstName || currentUser?.firstName,
          lastName: currentUser?.user?.lastName || currentUser?.lastName
        };
        
        setAttempts(prev => {
          const filtered = prev.filter(a => a.status === 'completed');
          return [attemptWithDefaults, ...filtered];
        });
        
        setCurrentAttempt(attemptWithDefaults);
        setSelectedAttemptId(attemptWithDefaults.id);
        return attemptWithDefaults;
      }
    } catch (error) {
      console.error('Error starting quiz attempt:', error);
    }
    return null;
  };

  const handleAnswerChange = (questionId, answer) => {
    if (!submitted && !answerResults[questionId]) {
      submitAnswer(questionId, answer);
    }
  };

  const handleSubmit = async () => {
    setSubmitError('');
    if (!selectedQuiz || Object.keys(answerResults).length !== selectedQuiz.questions.length) {
      setSubmitError('Please answer all questions before submitting.');
      return;
    }

    if (!currentAttempt) {
      setSubmitError('No active attempt found.');
      return;
    }

    const userId = currentUser?.user?.id || currentUser?.id;
    if (!userId) {
      setSubmitError('User ID not found. Please log in again.');
      return;
    }

    const attemptId = currentAttempt.id;
    const answers = selectedQuiz.questions.map(q => ({
      questionId: q.id,
      selectedAnswer: userAnswers[q.id],
      isCorrect: answerResults[q.id]?.isCorrect || false
    }));

    try {
      const response = await fetch(`${API_BASE_URL}/quiz/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({
          attemptId: attemptId,
          answers
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSubmitted(true);
        setSubmitError('');
        
        const updatedAttempt = {
          ...result,
          firstName: currentUser?.user?.firstName || currentUser?.firstName,
          lastName: currentUser?.user?.lastName || currentUser?.lastName
        };
        setAttempts(prev => {
          const existingIndex = prev.findIndex(a => a.id === attemptId);
          if (existingIndex >= 0) {
            const newAttempts = [...prev];
            newAttempts[existingIndex] = updatedAttempt;
            return newAttempts;
          } else {
            return [updatedAttempt, ...prev];
          }
        });
        setSelectedAttemptId(updatedAttempt.id);
        setCurrentAttempt(null);
      } else {
        const errorText = await response.text();
        console.error('Error submitting quiz:', errorText);
        setSubmitError(`Failed to submit quiz: ${errorText}`);
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      setSubmitError(`Error submitting quiz: ${error.message}`);
    }
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '2rem', color: '#343a40' }}>Training</h1>
      
      <div style={{ display: 'flex', flexWrap: 'wrap', backgroundColor: '#f8f9fa', padding: '0', borderRadius: '8px 8px 0 0', border: '1px solid #dee2e6', borderBottom: 'none' }}>
        <button 
          onClick={() => setMainTab('ppl')} 
          style={{ 
            padding: '1rem 2rem', 
            backgroundColor: mainTab === 'ppl' ? '#007bff' : 'transparent', 
            color: mainTab === 'ppl' ? 'white' : '#495057',
            border: 'none', 
            borderRadius: '8px 0 0 0',
            cursor: 'pointer',
            fontWeight: mainTab === 'ppl' ? 'bold' : 'normal',
            fontSize: '1rem'
          }}
        >
          PPL
        </button>
        {showExaminer && <button onClick={() => setMainTab('examiner')} aria-pressed={mainTab === 'examiner'} style={{ padding: '1rem', border: 'none', cursor: 'pointer', backgroundColor: mainTab === 'examiner' ? '#007bff' : 'transparent', color: mainTab === 'examiner' ? 'white' : '#495057' }}>AI Mock Oral Examiner</button>}
      </div>

      <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '0 0 8px 8px', padding: mainTab === 'examiner' ? '0.5rem' : '2rem' }}>
        {showExaminer && mainTab === 'examiner' && <MockExaminer />}
        {mainTab === 'ppl' && (
          <>
            <div style={{ display: 'flex', backgroundColor: '#e9ecef', padding: '0', borderRadius: '8px 8px 0 0' }}>
              <button 
                onClick={() => setSubTab('quizzes')} 
                style={{ 
                  padding: '0.75rem 1.5rem', 
                  backgroundColor: subTab === 'quizzes' ? '#f0f8ff' : 'transparent', 
                  color: '#495057',
                  border: '1px solid #ccc', 
                  borderBottom: subTab === 'quizzes' ? 'none' : '1px solid #ccc',
                  borderRadius: '8px 8px 0 0',
                  cursor: 'pointer',
                  fontWeight: subTab === 'quizzes' ? 'bold' : 'normal',
                  fontSize: '0.9rem'
                }}
              >
                Quizzes
              </button>
            </div>

            {subTab === 'quizzes' && (
              <div style={{ backgroundColor: '#f0f8ff', padding: '2rem', borderRadius: '0 8px 8px 8px', border: '1px solid #ccc' }}>
                <div style={{ marginBottom: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '1rem' }}>Select a Quiz:</label>
                    <select 
                      value={selectedQuizId} 
                      onChange={(e) => handleQuizSelect(e.target.value)}
                      style={{ 
                        width: '100%',
                        padding: '0.75rem', 
                        border: '1px solid #ccc', 
                        borderRadius: '6px',
                        fontSize: '1rem',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">-- Select a Quiz --</option>
                      {quizzes.map(quiz => (
                        <option key={quiz.id} value={quiz.id}>
                          {quiz.topic} - {quiz.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {attempts.length > 0 && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '1rem' }}>
                        Previous Attempts:
                      </label>
                      <select 
                        value={selectedAttemptId} 
                        onChange={(e) => {
                          setSelectedAttemptId(e.target.value);
                          setAttemptDetails(null); // Clear previous attempt details
                          if (e.target.value) {
                            fetchAttemptDetails(e.target.value);
                          }
                        }}
                        style={{ 
                          width: '100%',
                          padding: '0.75rem', 
                          border: '1px solid #ccc', 
                          borderRadius: '6px',
                          fontSize: '1rem',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">-- View Attempts --</option>
                        {attempts.map(attempt => {
                          const totalQuestions = attempt.totalQuestions || selectedQuiz?.questions?.length || 0;
                          const score = attempt.score || 0;
                          const isInstructor = currentUser?.user?.role === 'instructor' || currentUser?.user?.roleId === 3 || currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser';
                          const studentName = isInstructor && attempt.firstName && attempt.lastName 
                            ? `${attempt.firstName} ${attempt.lastName} - ` 
                            : '';
                          return (
                            <option key={attempt.id} value={attempt.id}>
                              {studentName}{attempt.completedAt ? formatLocalTime(attempt.completedAt) : 'In Progress ⏳'} - Score: {score}/{totalQuestions}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}
                </div>

                {selectedQuiz && !attemptDetails && !submitted && (
                  <>
                    <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', marginBottom: '2rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                      <h2 style={{ marginTop: 0, marginBottom: '1rem', color: '#007bff' }}>{selectedQuiz.name}</h2>
                      <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                        <strong>Category:</strong> {selectedQuiz.category} | <strong>Topic:</strong> {selectedQuiz.topic}
                      </p>

                      {selectedQuiz.questions.map((question, index) => (
                        <div key={question.id} style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                          <div style={{ marginBottom: '1rem', fontWeight: '500', fontSize: '1.1rem' }}>
                            {index + 1}. {question.question}
                          </div>
                          
                          {(question.image1 || question.image2) && (
                            <div style={{ marginBottom: '1rem' }}>
                              {question.image1 && (
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <button 
                                    onClick={() => {
                                      setSelectedReceiptImage(question.image1);
                                      setShowReceiptModal(true);
                                    }}
                                    style={{ 
                                      background: 'none',
                                      border: 'none',
                                      color: '#007bff',
                                      textDecoration: 'underline',
                                      cursor: 'pointer',
                                      fontSize: '0.9rem'
                                    }}
                                  >
                                    📎 View Image 1
                                  </button>
                                </div>
                              )}
                              {question.image2 && (
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <button 
                                    onClick={() => {
                                      setSelectedReceiptImage(question.image2);
                                      setShowReceiptModal(true);
                                    }}
                                    style={{ 
                                      background: 'none',
                                      border: 'none',
                                      color: '#007bff',
                                      textDecoration: 'underline',
                                      cursor: 'pointer',
                                      fontSize: '0.9rem'
                                    }}
                                  >
                                    📎 View Image 2
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {['A', 'B', 'C', 'D'].map(choice => (
                            <div key={choice} style={{ marginBottom: '0.75rem' }}>
                              <label style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                padding: '0.75rem', 
                                backgroundColor: userAnswers[question.id] === choice
                                  ? (answerResults[question.id]?.isCorrect ? '#d4edda' : '#f8d7da')
                                  : 'white',
                                borderRadius: '6px',
                                border: userAnswers[question.id] === choice
                                  ? (answerResults[question.id]?.isCorrect ? '2px solid #28a745' : '2px solid #dc3545')
                                  : '1px solid #dee2e6',
                                cursor: (submitted || answerResults[question.id]) ? 'default' : 'pointer',
                                transition: 'all 0.2s'
                              }}>
                                <input 
                                  type="radio" 
                                  name={`question-${question.id}`}
                                  value={choice}
                                  checked={userAnswers[question.id] === choice}
                                  onChange={() => handleAnswerChange(question.id, choice)}
                                  disabled={submitted || answerResults[question.id]}
                                  style={{ marginRight: '0.75rem', cursor: (submitted || answerResults[question.id]) ? 'default' : 'pointer' }}
                                />
                                <span style={{ flex: 1 }}>
                                  {choice}. {question[`choice${choice}`]}
                                </span>
                                {answerResults[question.id] && userAnswers[question.id] === choice && answerResults[question.id].isCorrect && (
                                  <span style={{ color: '#28a745', fontWeight: 'bold', marginLeft: '0.5rem' }}>✓ Correct</span>
                                )}
                                {answerResults[question.id] && userAnswers[question.id] === choice && !answerResults[question.id].isCorrect && (
                                  <span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '0.5rem' }}>✗ Wrong</span>
                                )}
                              </label>
                            </div>
                          ))}
                        </div>
                      ))}

                      {!submitted && (
                        <>
                          <button 
                            onClick={handleSubmit}
                            disabled={Object.keys(answerResults).length !== selectedQuiz.questions.length}
                            style={{ 
                              padding: '0.75rem 2rem', 
                              backgroundColor: Object.keys(answerResults).length !== selectedQuiz.questions.length ? '#6c757d' : '#007bff', 
                              color: 'white', 
                              border: 'none', 
                              borderRadius: '6px', 
                              cursor: Object.keys(answerResults).length !== selectedQuiz.questions.length ? 'not-allowed' : 'pointer',
                              fontSize: '1rem',
                              fontWeight: '500'
                            }}
                          >
                            Submit Quiz
                          </button>
                          {submitError && (
                            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '6px', border: '1px solid #f5c6cb' }}>
                              {submitError}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}

                {attemptDetails && (
                  <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', marginBottom: '2rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h2 style={{ marginTop: 0, marginBottom: '1rem', color: '#007bff' }}>Attempt Details</h2>
                    <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                      <strong>Student:</strong> {attemptDetails.firstName} {attemptDetails.lastName} | 
                      <strong>Score:</strong> {attemptDetails.score}/{attemptDetails.totalQuestions} | 
                      <strong>Status:</strong> {attemptDetails.status}
                    </p>

                    {attemptDetails.answers && attemptDetails.answers.map((answer, index) => {
                      const question = selectedQuiz.questions.find(q => q.id === answer.questionId);
                      if (!question) return null;
                      
                      return (
                        <div key={`attempt-${attemptDetails.id}-q-${answer.questionId}-${index}`} style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                          <div style={{ marginBottom: '1rem', fontWeight: '500', fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>{index + 1}. {question.question}</span>
                            <span style={{ color: answer.isCorrect ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                              {answer.isCorrect ? 'Correct' : 'Incorrect'}
                            </span>
                          </div>
                          
                          {(question.image1 || question.image2) && (
                            <div style={{ marginBottom: '1rem' }}>
                              {question.image1 && (
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <button 
                                    onClick={() => {
                                      setSelectedReceiptImage(question.image1);
                                      setShowReceiptModal(true);
                                    }}
                                    style={{ 
                                      background: 'none',
                                      border: 'none',
                                      color: '#007bff',
                                      textDecoration: 'underline',
                                      cursor: 'pointer',
                                      fontSize: '0.9rem'
                                    }}
                                  >
                                    📎 View Image 1
                                  </button>
                                </div>
                              )}
                              {question.image2 && (
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <button 
                                    onClick={() => {
                                      setSelectedReceiptImage(question.image2);
                                      setShowReceiptModal(true);
                                    }}
                                    style={{ 
                                      background: 'none',
                                      border: 'none',
                                      color: '#007bff',
                                      textDecoration: 'underline',
                                      cursor: 'pointer',
                                      fontSize: '0.9rem'
                                    }}
                                  >
                                    📎 View Image 2
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {['A', 'B', 'C', 'D'].map(choice => (
                            <div key={choice} style={{ marginBottom: '0.75rem' }}>
                              <label style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                padding: '0.75rem', 
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                border: answer.selectedAnswer === choice ? '2px solid #007bff' : '1px solid #dee2e6',
                                cursor: 'default'
                              }}>
                                <input 
                                  type="radio" 
                                  name={`attempt-question-${answer.questionId}`}
                                  value={choice}
                                  checked={answer.selectedAnswer === choice}
                                  disabled={true}
                                  style={{ marginRight: '0.75rem', cursor: 'default' }}
                                />
                                <span style={{ flex: 1 }}>
                                  {choice}. {question[`choice${choice}`]}
                                </span>
                                {answer.selectedAnswer === choice && (
                                  <span style={{ color: '#007bff', fontWeight: 'bold', marginLeft: '0.5rem' }}>← SELECTED</span>
                                )}
                              </label>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Image Modal */}
      {showReceiptModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Question Image</h3>
              <button onClick={() => setShowReceiptModal(false)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', padding: '0.25rem' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', textAlign: 'center' }}>
              <img src={selectedReceiptImage} alt="Question Image" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Training;